import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  DateParam,
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params';
import moment from 'moment';
import {
  Grid,
  MenuItem,
  Stack,
  TextField,
  Box,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { DatePicker } from '@mui/x-date-pickers';

import { BuyoutStatuses } from '@/types';
import { AppBar, DebouncedTextField, Empty, Error, Loader } from '@/components';
import { CheckFlat, TimesFlat } from '@/assets/icons';
import { OrderDirections, ORDER_DIRECTIONS } from '@/utils/order-directions';

import { BuyoutsTable } from './components';
import {useLazyGetBuyoutsQuery} from "@/services/buyout";

export const BUYOUT_STATUSES = [
  {
    label: 'Проведён',
    value: BuyoutStatuses.CARRIED_OUT,
    icon: <CheckFlat sx={{ fontSize: 16 }} color="success" />,
  },
  {
    label: 'Отменён',
    value: BuyoutStatuses.CANCELLED,
    icon: <TimesFlat sx={{ fontSize: 16 }} color="error" />,
  },
];

const BuyoutsPage: React.FC = () => {
  const { pledgeId } = useParams<{ pledgeId: string }>();
  const [
    { search, order_direction, page, date_from, date_to, status },
    setQuery,
  ] = useQueryParams({
    search: withDefault(StringParam, ''),
    page: withDefault(NumberParam, 1),
    order_direction: withDefault(StringParam, OrderDirections.DESC),
    status: withDefault(StringParam, ''),
    date_from: withDefault(
        DateParam,
        pledgeId ? null : moment().toDate()
    ),
    date_to: withDefault(DateParam, moment().toDate()),
  });

  // ленивый хук для запроса
  const [
    trigger,
    { data: buyouts, isLoading, isFetching, isError, isSuccess },
  ] = useLazyGetBuyoutsQuery();

  const [hasSearched, setHasSearched] = useState(false);

  const buildRequest = (overridePage = page) => ({
    params: {
      search: search || null,
      order_direction,
      page: overridePage,
      order_by: 'id',
      pledge_id: pledgeId ? +pledgeId : null,
      status,
      date_from: date_from
          ? moment(date_from).format('YYYY-MM-DD')
          : null,
      date_to: date_to
          ? moment(date_to).format('YYYY-MM-DD')
          : null,
    },
  });

  const onSearch = () => {
    setQuery({ page: 1 });
    setHasSearched(true);
    trigger(buildRequest(1));
  };

  const onChangePage = (newPage: number) => {
    setQuery({ page: newPage });
    trigger(buildRequest(newPage));
  };

  return (
      <>
        <AppBar title="Выкупы" />

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Stack direction="row" spacing={3} alignItems="flex-start">
              <Grid container spacing={2}>
                {/* Поиск */}
                <Grid item lg={3} md={3} sm={6} xs={12}>
                  <DebouncedTextField
                      type="search"
                      label="Поиск"
                      placeholder="Введите значение"
                      value={search}
                      onChange={e => setQuery({ search: e.target.value, page: 1 })}
                      variant="filled"
                      fullWidth
                      size="small"
                  />
                </Grid>

                {/* Сортировка */}
                <Grid item lg={3} md={3} sm={6} xs={12}>
                  <TextField
                      select
                      label="Сортировать по"
                      value={order_direction}
                      disabled={isLoading}
                      onChange={e => setQuery({ order_direction: e.target.value })}
                      variant="filled"
                      fullWidth
                      size="small"
                  >
                    {ORDER_DIRECTIONS.map(({ value, label }) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {/* Статус */}
                <Grid item lg={3} md={3} sm={6} xs={12}>
                  <TextField
                      select
                      label="Статус"
                      value={status}
                      disabled={isLoading}
                      onChange={e => setQuery({ status: e.target.value })}
                      variant="filled"
                      fullWidth
                      size="small"
                  >
                    <MenuItem value="">Все</MenuItem>
                    {BUYOUT_STATUSES.map(({ value, label }) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {/* Дата от */}
                <Grid item lg={3} md={3} sm={6} xs={12}>
                  <DatePicker
                      mask="__.__.____"
                      label="Дата от"
                      value={date_from}
                      minDate={moment(date_to).subtract(6, 'months')}
                      maxDate={moment(date_to)}
                      onChange={val =>
                          setQuery({
                            date_from: val ? moment(val).toDate() : undefined,
                          })
                      }
                      disabled={isLoading || isFetching}
                      renderInput={params => (
                          <TextField
                              {...params}
                              variant="filled"
                              size="small"
                              fullWidth
                          />
                      )}
                  />
                </Grid>

                {/* Дата до */}
                <Grid item lg={3} md={3} sm={6} xs={12}>
                  <DatePicker
                      mask="__.__.____"
                      label="Дата до"
                      value={date_to}
                      maxDate={moment()}
                      onChange={val =>
                          setQuery({
                            date_to: val ? moment(val).toDate() : undefined,
                          })
                      }
                      disabled={isLoading || isFetching}
                      renderInput={params => (
                          <TextField
                              {...params}
                              variant="filled"
                              size="small"
                              fullWidth
                          />
                      )}
                  />
                </Grid>

                {/* Кнопка Поиск */}
                <Grid item lg={3} md={3} sm={6} xs={12}>
                  <LoadingButton
                      loading={isFetching}
                      disabled={isFetching}
                      onClick={onSearch}
                      size="large"
                      variant="contained"
                      fullWidth
                  >
                    Поиск
                  </LoadingButton>
                </Grid>
              </Grid>
            </Stack>
          </Grid>

          {/* Результаты */}
          <Grid item xs={12}>
            {!hasSearched ? (
                <Empty text="Нажмите «Поиск», чтобы увидеть данные" />
            ) : isLoading ? (
                <Loader />
            ) : isError ? (
                <Error text="Произошла ошибка с загрузкой выкупов! Попробуйте позже" />
            ) : isSuccess && buyouts?.data?.length ? (
                <BuyoutsTable
                    list={buyouts}
                    page={page}
                    disabled={isFetching}
                    onChangePage={onChangePage}
                />
            ) : (
                <Empty text="Выкупы отсутствуют" />
            )}
          </Grid>
        </Grid>
      </>
  );
};

export default BuyoutsPage;
